<script lang="ts" setup>
const route = useRoute();
const localePath = useLocalePath();

const emit = defineEmits<{
  (e: 'hoverEnter', id: string): void,
  (e: 'hoverExit', id: string): void
}>()

const props = defineProps({
  navRoute: {type: String, required: true},
  name: {type: String, required: true},
  // routes under this section that another nav button claims as its own
  except: {type: Array as () => string[], default: () => []},
  // whether this button reveals the shared details panel, and whether that
  // panel is currently showing this button's section
  hasDetails: {type: Boolean, default: false},
  expanded: {type: Boolean, default: false}
});

function emitEnter(): void {
  emit('hoverEnter', props.navRoute);
}

function emitExit(): void {
  emit('hoverExit', props.navRoute);
}

// Match on route base names (locale-independent) instead of substrings,
// so e.g. navRoute "refuge" highlights /refuge/* but nothing that merely
// contains the word, and excluded routes light up their own button only.
const getRouteBaseName = useRouteBaseName();
const toBaseName = (r: string) => r.replace(/^\//, '').replace(/\//g, '-');

const onRoute = computed(() => {
  const base = getRouteBaseName(route) ?? '';
  if (props.except.some(e => base === toBaseName(e))) return false;
  const target = toBaseName(props.navRoute);
  return base === target || base.startsWith(`${target}-`);
})
</script>

<template>
  <div class="nav-button-wrapper"
       @mouseenter="emitEnter" @mouseleave="emitExit"
       @focusin="emitEnter" @focusout="emitExit">
    <nuxt-link :to="localePath(navRoute)" class="nav-link" :class="{ 'is-active': onRoute }"
               :aria-current="onRoute ? 'page' : undefined"
               :aria-controls="hasDetails ? 'nav-details-panel' : undefined"
               :aria-expanded="hasDetails ? expanded : undefined">
      {{ name }}
      <span class="underline"></span>
    </nuxt-link>
  </div>
</template>

<style lang="sass" scoped>
.nav-button-wrapper
  display: inline-block

.nav-link
  font-family: 'Montserrat', sans-serif
  color: $color-text-main
  font-size: 0.92rem
  font-weight: 600
  text-decoration: none
  padding: 0.5rem 0
  position: relative
  transition: color $transition-fast
  display: flex
  flex-direction: column
  align-items: center

  &:hover
    color: $color-primary

    .underline
      width: 100%
      opacity: 1

  &.is-active
    color: $color-primary

    .underline
      width: 100%
      opacity: 1

.underline
  position: absolute
  bottom: 0
  width: 0
  height: 2px
  background-color: $color-primary
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1)
  opacity: 0

.dark-mode .nav-link
  color: $color-text-light

  &:hover
    color: $color-primary

@media (max-width: 480px)
  .nav-link
    font-size: 0.9rem

</style>
